/**
 * Atlas — Notification bell.
 *
 * Sits in the topbar next to the avatar. Polls /notifications/unread-count
 * every 30s when the user is signed in, renders a red badge when there's
 * anything unread. Click → /notifications inbox.
 *
 * Polling is intentionally simple — Phase 2 (push) will deliver real-time
 * and we can drop the interval. 30s is enough latency for the inbox tab
 * use case without hammering the API.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsAPI } from '../../services/api';

const POLL_MS = 30_000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) { setUnread(0); return undefined; }
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const { data } = await notificationsAPI.unreadCount();
        if (!cancelled) setUnread(data?.unread ?? 0);
      } catch (_) {
        // Network blip — keep showing the last known count instead of
        // resetting to 0 (which would look like "you've read everything").
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS);
      }
    };

    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  return (
    <button
      onClick={() => navigate('/notifications')}
      aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
      style={{
        position: 'relative',
        background: 'transparent', border: 'none', cursor: 'pointer',
        width: 36, height: 36, borderRadius: 8,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, color: 'var(--ink-2)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-2)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span>🔔</span>
      {unread > 0 && (
        <span style={{
          position: 'absolute', top: 4, right: 4,
          minWidth: 16, height: 16, padding: '0 4px',
          borderRadius: 8, background: 'var(--crimson)',
          color: '#fff', fontSize: 10, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1,
        }}>
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}
